import { Avatar, Tooltip } from '@heroui/react';

import { initialsOf } from '../../services/initials';

// Πρόσωπα σε λίγο χώρο: οι ανάδοχοι ενός καθηκόντος πάνω στην κάρτα του.
//
// ⚠ **ΤΟ ΟΝΟΜΑ ΠΡΕΠΕΙ ΝΑ ΦΤΑΝΕΙ ΚΑΙ ΧΩΡΙΣ ΠΟΝΤΙΚΙ.** Η εικόνα αντικαθιστά ένα
// όνομα, δηλαδή κρύβει πληροφορία πίσω από αισθητήρα που δεν έχουν όλοι. Το
// `title` του HTML —ο εύκολος δρόμος— εμφανίζεται **μόνο** με το ποντίκι: δεν
// ανοίγει με το πληκτρολόγιο και δεν εκφωνείται αξιόπιστα. Γι' αυτό ο σκανδαλιστής
// είναι **κουμπί**, που δέχεται εστίαση, και η υπόδειξη ανοίγει και στην εστίαση.
//
// Και ανεξάρτητα από την υπόδειξη, το `aria-label` λέει το όνομα ολόκληρο: η
// υπόδειξη είναι **οπτική** διευκόλυνση, όχι ο φορέας της πληροφορίας. Δύο
// διαφορετικά κανάλια, γιατί αποτυγχάνουν διαφορετικά.
//
// **Το κουμπί δεν κάνει τίποτα** — και είναι σκόπιμο. Ενέργεια πάνω στο πρόσωπο
// (φιλτράρισμα, άνοιγμα προφίλ) θα ήταν δεύτερη σκανδάλη μέσα σε κάρτα που έχει
// ήδη μία, και το πάτημα θα ανταγωνιζόταν το άνοιγμα του καθηκόντος.

/** Πόσα πρόσωπα δείχνονται πριν αρχίσει η μέτρηση. */
const VISIBLE = 3;

const nameOf = (user) => user?.fullName || user?.username || '—';

/**
 * Το `sizeClass` δίνει στον καλούντα **ρητό** έλεγχο διαστάσεων, πέρα από τα
 * μεγέθη της βιβλιοθήκης.
 *
 * Χρειάστηκε γιατί το μικρότερο μέγεθός της (`xs`) ήταν ακόμη μεγάλο για την
 * κάρτα ενός καθηκόντος, όπου ο ανάδοχος είναι **δευτερεύουσα** πληροφορία. Δεν
 * αντικαθιστά το `size`: εκείνο μένει και ορίζει τυπογραφία και αναλογίες, ενώ
 * αυτό επιβάλλεται από πάνω μόνο όπου δίνεται.
 */
export default function AvatarStack({ users = [], label, size = 'xs', sizeClass = '' }) {
    const people = users.filter(Boolean);
    if (people.length === 0) return null;

    const shown = people.slice(0, VISIBLE);
    const hidden = people.length - shown.length;

    return (
        <span className="flex items-center">
            {shown.map((user, index) => (
                <Tooltip key={user.id ?? user.username ?? index}>
                    <Tooltip.Trigger
                        // **Χωρίς `elementType`.** Ο σκανδαλιστής της βιβλιοθήκης
                        // αποδίδει `div` και βάζει ο ίδιος `role="button"` και
                        // δείκτη εστίασης — είναι ήδη κουμπί για τον αναγνώστη
                        // οθόνης και ήδη προσιτός με `Tab`. Το `elementType` δεν
                        // αναγνωρίζεται και **περνούσε ως χαρακτηριστικό στο
                        // DOM**: προειδοποίηση του React, και ένα ψεύτικο
                        // χαρακτηριστικό στη σελίδα.
                        aria-label={label ? label(nameOf(user)) : nameOf(user)}
                        // Η επικάλυψη είναι **οπτική** και όχι δομική: κάθε
                        // πρόσωπο μένει χωριστός στόχος εστίασης, με ορατό
                        // περίγραμμα όταν φτάνει με `Tab`.
                        className="-ms-1.5 rounded-full first:ms-0 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-focus"
                    >
                        <Avatar size={size} className={`ring-2 ring-surface ${sizeClass}`.trim()}>
                            {user.avatar && <Avatar.Image src={user.avatar} alt="" />}
                            <Avatar.Fallback>{initialsOf(user)}</Avatar.Fallback>
                        </Avatar>
                    </Tooltip.Trigger>
                    <Tooltip.Content>{nameOf(user)}</Tooltip.Content>
                </Tooltip>
            ))}

            {hidden > 0 && (
                <span
                    className="-ms-1.5 flex h-6 min-w-6 items-center justify-center rounded-full border border-border bg-surface px-1 text-xs font-medium ring-2 ring-surface"
                    title={people.slice(VISIBLE).map(nameOf).join(', ')}
                >
                    {`+${hidden}`}
                </span>
            )}
        </span>
    );
}
